import { useId, useState } from "react";
import { redirect, useFetcher, useParams, useSearchParams } from "react-router";
import type { ActionFunctionArgs, LoaderFunctionArgs } from "react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import type { Feedback } from "@repo/shared-types";
import { api } from "@/lib/api";
import { STATUSES, kindLabel } from "@/lib/feedback";
import { queries } from "@/lib/queries";
import { read } from "@/lib/query";
import { errorMessage } from "@/lib/session";
import { toast } from "@/lib/toast";
import { PageTitle } from "@/components/brand";
import { CogMenu, MenuButton } from "@/components/cog-menu";
import { FeedbackMeta, MeToo, ReplyNote } from "@/components/feedback-card";
import { ErrorNote, SegmentedControl } from "@/components/form";
import { ConfirmDialog } from "@/components/modal";
import { BackLink, TopBar } from "@/components/navigation";
import { PhotoView } from "@/components/photos";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Textarea } from "@/components/ui/textarea";
import { feedbackSession } from "@/routes/feedback";
import { useSession } from "@/routes/root";

/**
 * One feedback (0091): all of its words, its screenshot laid out by its size,
 * the runner's reply and Me too. Its sender and the runner delete it from the
 * cog, asked first; the runner — `Me.runsFeedback`, which the api decides —
 * also sets its status, saved as a segment is tapped, and writes the reply.
 * What the page offers is presentation: the api answers 404 to anybody else.
 *
 * A screenshot that did not go up after the feedback was stored lands here
 * with `?picture=failed`.
 */

export async function feedbackItemLoader({ request, params }: LoaderFunctionArgs) {
  await feedbackSession(request);
  await read(queries.feedbackItem(params.id ?? ""));
  return null;
}

type Outcome = { intent?: string; error?: string };

export async function feedbackItemAction({ request, params }: ActionFunctionArgs): Promise<Outcome | Response> {
  const form = await request.formData();
  const id = params.id ?? "";
  const intent = String(form.get("intent") ?? "");
  try {
    switch (intent) {
      case "status":
        await api.feedback.update(id, { status: String(form.get("status") ?? "") });
        toast("Status saved.");
        return { intent };
      // Emptied, it clears.
      case "reply": {
        const reply = String(form.get("reply") ?? "").trim();
        await api.feedback.update(id, { reply });
        toast(reply ? "Reply saved." : "Reply cleared.");
        return { intent };
      }
      case "delete":
        await api.feedback.remove(id);
        toast("Feedback deleted.");
        return redirect("/feedback");
      default:
        throw new Response("Bad Request", { status: 400 });
    }
  } catch (error) {
    if (error instanceof Response) throw error;
    return { intent, error: errorMessage(error, "Could not save that.") };
  }
}

export default function FeedbackPage() {
  const id = useParams().id ?? "";
  const session = useSession();
  const { data: feedback } = useSuspenseQuery(queries.feedbackItem(id));
  // The loader let the page draw only with a session.
  return session && <FeedbackItem feedback={feedback} runner={session.runsFeedback} />;
}

function FeedbackItem({ feedback, runner }: { feedback: Feedback; runner: boolean }) {
  const [search] = useSearchParams();
  const [deleting, setDeleting] = useState(false);
  const canDelete = feedback.mine || runner;
  const picture = feedback.picture;

  return (
    <section className="space-y-6">
      <PageTitle of={[kindLabel(feedback.kind), "Feedback"]} />
      <div className="space-y-2">
        <TopBar>
          <BackLink to="/feedback">Feedback</BackLink>
          {canDelete && (
            <CogMenu label="Feedback menu">
              {(close) => (
                <MenuButton
                  danger
                  onClick={() => {
                    close();
                    setDeleting(true);
                  }}
                >
                  Delete
                </MenuButton>
              )}
            </CogMenu>
          )}
        </TopBar>
        <h1 className="page-title">{kindLabel(feedback.kind)}</h1>
        <FeedbackMeta feedback={feedback} now={Date.now()} kind={false} />
      </div>

      {search.get("picture") === "failed" && (
        <ErrorNote message="Your feedback was sent, but the screenshot did not go up." />
      )}

      <Card>
        <CardContent className="space-y-4">
          <p className="text-base whitespace-pre-line wrap-anywhere">{feedback.message}</p>
          {/* An upload that failed leaves its row pending until the sweep:
              the note above says it did not go, so nothing says it is coming. */}
          {picture &&
            !(picture.status === "pending" && search.get("picture") === "failed") &&
            (picture.status === "pending" ? (
              <p className="text-sm text-muted-foreground">Your screenshot is on its way.</p>
            ) : (
              <PhotoView photo={picture} alt="Screenshot" spaceName={null} />
            ))}
        </CardContent>
      </Card>

      {runner ? <RunnerControls feedback={feedback} /> : feedback.reply && <ReplyNote reply={feedback.reply} />}

      <MeToo feedback={feedback} />

      {deleting && (
        <ConfirmDialog
          title="Delete this feedback?"
          fields={{ intent: "delete" }}
          confirm="Delete"
          onClose={() => setDeleting(false)}
        >
          It goes from the board for everyone, with its me-toos and screenshot. This cannot be undone.
        </ConfirmDialog>
      )}
    </section>
  );
}

/** The runner's: a status saved as a segment is tapped, and the one reply. */
function RunnerControls({ feedback }: { feedback: Feedback }) {
  const status = useFetcher<Outcome>();
  const reply = useFetcher<Outcome>();
  const id = useId();
  const asked = status.formData?.get("status");
  const shown = typeof asked === "string" ? asked : feedback.status;

  return (
    <Card>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <SegmentedControl
            label="Status"
            name="status"
            options={STATUSES.map(({ value, label }) => ({ value, label }))}
            value={shown}
            onChange={(next) => status.submit({ intent: "status", status: next }, { method: "post" })}
          />
          {status.data?.error && <FieldError>{status.data.error}</FieldError>}
        </div>
        {/* Keyed by what is saved, so the box shows it again after a save. */}
        <reply.Form method="post" key={feedback.reply ?? ""} className="space-y-3">
          <input type="hidden" name="intent" value="reply" />
          <Field>
            <FieldLabel htmlFor={id}>Reply</FieldLabel>
            <Textarea id={id} name="reply" rows={4} defaultValue={feedback.reply ?? ""} />
            {reply.data?.error && <FieldError>{reply.data.error}</FieldError>}
          </Field>
          <div className="flex justify-end">
            <Button type="submit" size="lg" disabled={reply.state !== "idle"}>
              Save
            </Button>
          </div>
        </reply.Form>
      </CardContent>
    </Card>
  );
}
